import { useState, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getApiBase } from "../apiBase";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCheck, FaArrowRight } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { GoogleLogin } from "@react-oauth/google";

const GOLD = "#C9A84C";
const GOLD_DIM = "rgba(201,168,76,0.12)";
const GOLD_BORDER = "rgba(201,168,76,0.25)";

const STEPS = [
  { label: "live chat", terminalTitle: "The only platform where building and validating your AI assistant happen in the same place.", desc: "From idea to AI assistant — without the complexity" },
  { label: "analytics", terminalTitle: "Stop guessing how your AI performs. Start knowing.", desc: "Stop guessing. Start knowing how your AI performs." },
  { label: "monitoring", terminalTitle: "Build, evaluate, and monitor AI assistants at scale.", desc: "Set up in minutes." },
  { label: "evaluation", terminalTitle: "The only platform where building and validating your AI assistant happen in the same place.", desc: "Answer customer questions accurately, every time" },
  { label: "deployment", terminalTitle: "Go live in minutes, no engineering team needed.", desc: "Handle real volume without performance anxiety" },
];

const evalTests = [
  { query: "How can I create a new AI chatbot for my company?", status: "pass", confidence: 97 },
  { query: "Can I upload PDFs and documents to train my chatbot?", status: "pass", confidence: 95 },
  { query: "How do I customize branding for my chatbot widget?", status: "pass", confidence: 93 },
  { query: "Does Droog AI support WhatsApp and website integrations?", status: "pass", confidence: 91 },
  { query: "How do I set up enterprise-level access control for my team?", status: "warn", confidence: 68 },
  { query: "Can I export all customer conversations and reports?", status: "pass", confidence: 89 },
  { query: "Does Droog AI provide SLA support for enterprise customers?", status: "warn", confidence: 64 },
  { query: "How do I integrate Droog AI with my CRM system?", status: "pass", confidence: 88 },
  { query: "Can I use multilingual support for customer conversations?", status: "fail", confidence: 34 },
];

const chatMessages = [
  { type: "user", msg: "How do I create my first AI bot?", time: "09:13 AM", avatar: "U" },
  { type: "bot", msg: "Just upload your content and I'll handle the rest — ready in under 2 minutes.", time: "09:13 AM", avatar: "B" },
  { type: "user", msg: "What happens when the bot doesn't know an answer?", time: "5m ago", avatar: "U" },
  { type: "bot", msg: "It escalates to your team automatically with full context.", time: "5m ago", avatar: "B" },
];

const deploySteps = [
  { label: "Create Bot", done: true },
  { label: "Upload KB", done: true },
  { label: "Gen Prompts", done: true },
  { label: "Go Live", done: false, active: true },
];

function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1440);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

function getScale(width) {
  if (width >= 2560) return { tier: "ultrawide", factor: 1.5 };
  if (width >= 1920) return { tier: "fullhd", factor: 1.28 };
  if (width >= 1600) return { tier: "large", factor: 1.14 };
  if (width >= 1440) return { tier: "desktop", factor: 1.06 };
  return { tier: "laptop", factor: 1 };
}

const DemoWindow = ({ activeStep, scale }) => {
  const f = scale.factor;
  const headerLabel = ["live chat", "analytics", "monitoring", "evaluation · run #284 · education-consultant-v3", "deployment"][activeStep];
  const windowHeight = Math.round(260 * f);
  const evalRows = scale.tier === "laptop" ? 5 : scale.tier === "desktop" ? 6 : scale.tier === "large" ? 7 : 8;

  return (
    <div style={{
      background: "#0d0d0d",
      border: "1px solid rgba(255,255,255,0.09)",
      borderRadius: Math.round(10 * f),
      overflow: "hidden",
      boxShadow: `0 ${Math.round(40 * f)}px ${Math.round(120 * f)}px rgba(0,0,0,0.9)`,
      width: "100%",
    }}>
      <style>{`
        input::placeholder, textarea::placeholder {
          color: rgba(255,255,255,0.12) !important;
          opacity: 1;
        }
        input::-webkit-input-placeholder, textarea::-webkit-input-placeholder {
          color: rgba(255,255,255,0.12) !important;
        }
        input::-moz-placeholder, textarea::-moz-placeholder {
          color: rgba(255,255,255,0.12) !important;
        }
        input:-ms-input-placeholder, textarea:-ms-input-placeholder {
          color: rgba(255,255,255,0.12) !important;
        }
      `}</style>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: `${Math.round(9 * f)}px ${Math.round(14 * f)}px`,
        background: "rgba(255,255,255,0.03)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ display: "flex", gap: Math.round(6 * f) }}>
          {["rgba(255,96,96,0.5)", "rgba(255,189,68,0.5)", "rgba(80,200,100,0.5)"].map((c, i) => (
            <div key={i} style={{ width: Math.round(11 * f), height: Math.round(11 * f), borderRadius: "50%", background: c }} />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: Math.round(6 * f) }}>
          <div style={{ width: Math.round(6 * f), height: Math.round(6 * f), borderRadius: "50%", background: "#4ade80" }} />
          <AnimatePresence mode="wait">
            <motion.span key={activeStep} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ fontFamily: "monospace", fontSize: Math.round(10 * f), color: "rgba(255,255,255,0.4)", letterSpacing: "0.03em" }}>
              {headerLabel}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>

      <div style={{ height: windowHeight, overflow: "hidden" }}>
        <AnimatePresence mode="wait">
          {activeStep === 0 && (
            <motion.div key="chat" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }} style={{ padding: `${Math.round(14 * f)}px ${Math.round(16 * f)}px`, display: "flex", flexDirection: "column", gap: Math.round(10 * f) }}>
              {chatMessages.map((msg, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: msg.type === "user" ? -10 : 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }} style={{ display: "flex", alignItems: "flex-start", gap: Math.round(8 * f), flexDirection: msg.type === "bot" ? "row-reverse" : "row" }}>
                  <div style={{ width: Math.round(22 * f), height: Math.round(22 * f), borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(8 * f), fontWeight: 700, background: msg.type === "user" ? "rgba(130,80,180,0.5)" : GOLD_DIM, border: `1px solid ${msg.type === "user" ? "rgba(130,80,180,0.4)" : GOLD_BORDER}`, color: msg.type === "user" ? "rgba(200,160,255,0.9)" : GOLD }}>{msg.avatar}</div>
                  <div style={{ maxWidth: "78%", padding: `${Math.round(7 * f)}px ${Math.round(11 * f)}px`, borderRadius: Math.round(10 * f), background: msg.type === "user" ? "rgba(255,255,255,0.05)" : GOLD_DIM, border: `1px solid ${msg.type === "user" ? "rgba(255,255,255,0.08)" : GOLD_BORDER}` }}>
                    <p style={{ margin: 0, fontSize: Math.round(11 * f), lineHeight: 1.55, color: msg.type === "user" ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.85)" }}>{msg.msg}</p>
                    <span style={{ fontSize: Math.round(9 * f), color: "rgba(255,255,255,0.2)", marginTop: Math.round(3 * f), display: "block" }}>{msg.time}</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeStep === 1 && (
            <motion.div key="analytics" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }} style={{ padding: `${Math.round(14 * f)}px ${Math.round(16 * f)}px`, display: "flex", flexDirection: "column", gap: Math.round(10 * f) }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: Math.round(8 * f) }}>
                {[{ title: "Faster Replies", value: "Instant" }, { title: "Better Support", value: "24/7" }, { title: "Less Manual Work", value: "80%" }, { title: "Happy Customers", value: "4.8/5" }].map((m, i) => (
                  <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.07 }} style={{ padding: `${Math.round(10 * f)}px ${Math.round(12 * f)}px`, borderRadius: Math.round(8 * f), background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                    <p style={{ margin: `0 0 ${Math.round(3 * f)}px`, fontSize: Math.round(8 * f), color: GOLD, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace" }}>{m.title}</p>
                    <p style={{ margin: 0, fontSize: Math.round(18 * f), fontWeight: 700, color: "rgba(255,255,255,0.9)", letterSpacing: "-0.02em" }}>{m.value}</p>
                  </motion.div>
                ))}
              </div>
              {[{ label: "Customer satisfaction improved", val: 94, color: GOLD }, { label: "Support workload reduced", val: 87, color: "#4ade80" }, { label: "Faster issue resolution", val: 91, color: "#a78bfa" }].map((bar, i) => (
                <div key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: Math.round(3 * f) }}>
                    <span style={{ fontSize: Math.round(10 * f), color: "rgba(255,255,255,0.5)" }}>{bar.label}</span>
                    <span style={{ fontSize: Math.round(10 * f), fontFamily: "monospace", color: bar.color }}>{bar.val}%</span>
                  </div>
                  <div style={{ height: Math.round(3 * f), background: "rgba(255,255,255,0.07)", borderRadius: 2, overflow: "hidden" }}>
                    <motion.div style={{ height: "100%", background: bar.color, borderRadius: 2 }} initial={{ width: 0 }} animate={{ width: `${bar.val}%` }} transition={{ duration: 1, delay: i * 0.15 }} />
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {activeStep === 2 && (
            <motion.div key="monitor" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }} style={{ padding: `${Math.round(14 * f)}px ${Math.round(16 * f)}px`, display: "grid", gridTemplateColumns: "1fr 1fr", gap: Math.round(12 * f) }}>
              <div>
                <p style={{ margin: `0 0 ${Math.round(8 * f)}px`, fontSize: Math.round(9 * f), color: GOLD, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace" }}>Documents Uploaded</p>
                {[{ name: "Product FAQ.md", size: "24KB", status: "indexed" }, { name: "Onboarding.pdf", size: "156KB", status: "indexed" }, { name: "API Docs.pdf", size: "89KB", status: "indexing" }, { name: "Pricing.md", size: "12KB", status: "queued" }].map((file, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.09 }} style={{ display: "flex", alignItems: "center", gap: Math.round(7 * f), padding: `${Math.round(6 * f)}px ${Math.round(8 * f)}px`, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: Math.round(7 * f), marginBottom: Math.round(5 * f) }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: Math.round(10 * f), color: "rgba(255,255,255,0.7)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file.name}</p>
                      <p style={{ margin: 0, fontSize: Math.round(8 * f), color: "rgba(255,255,255,0.25)" }}>{file.size}</p>
                    </div>
                    <span style={{ fontSize: Math.round(8 * f), padding: `2px ${Math.round(6 * f)}px`, borderRadius: 20, background: file.status === "indexed" ? "rgba(74,222,128,0.1)" : file.status === "indexing" ? GOLD_DIM : "rgba(255,255,255,0.06)", color: file.status === "indexed" ? "#4ade80" : file.status === "indexing" ? GOLD : "rgba(255,255,255,0.3)", border: `1px solid ${file.status === "indexed" ? "rgba(74,222,128,0.2)" : file.status === "indexing" ? GOLD_BORDER : "rgba(255,255,255,0.08)"}` }}>{file.status}</span>
                  </motion.div>
                ))}
              </div>
              <div>
                <p style={{ margin: `0 0 ${Math.round(8 * f)}px`, fontSize: Math.round(9 * f), color: "rgba(160,100,220,0.85)", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace" }}>Live Monitor</p>
                {[{ label: "Active sessions", val: "23", color: "#4ade80" }, { label: "Msgs/min", val: "47", color: GOLD }, { label: "Error rate", val: "0.2%", color: GOLD }].map((item, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 + 0.15 }} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${Math.round(7 * f)}px ${Math.round(10 * f)}px`, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: Math.round(7 * f), marginBottom: Math.round(5 * f) }}>
                    <span style={{ fontSize: Math.round(10 * f), color: "rgba(255,255,255,0.45)" }}>{item.label}</span>
                    <motion.span animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.4 }} style={{ fontSize: Math.round(12 * f), fontWeight: 700, fontFamily: "monospace", color: item.color }}>{item.val}</motion.span>
                  </motion.div>
                ))}
                <div style={{ marginTop: Math.round(4 * f), padding: `${Math.round(8 * f)}px ${Math.round(10 * f)}px`, borderRadius: Math.round(7 * f), background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.15)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: Math.round(6 * f), marginBottom: 2 }}>
                    <motion.div animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.2, repeat: Infinity }} style={{ width: Math.round(5 * f), height: Math.round(5 * f), borderRadius: "50%", background: "#4ade80" }} />
                    <span style={{ fontSize: Math.round(9 * f), color: "#4ade80", fontWeight: 500 }}>System healthy</span>
                  </div>
                  <p style={{ margin: 0, fontSize: Math.round(8 * f), color: "rgba(255,255,255,0.3)" }}>All services operational · 99.9% uptime</p>
                </div>
              </div>
            </motion.div>
          )}

          {activeStep === 3 && (
            <motion.div key="eval" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 0, padding: `${Math.round(8 * f)}px ${Math.round(16 * f)}px ${Math.round(6 * f)}px`, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                {["Question", "Accuracy", "%"].map((h, i) => (
                  <span key={i} style={{ fontFamily: "monospace", fontSize: Math.round(9 * f), color: "rgba(255,255,255,0.25)", letterSpacing: "0.08em", textTransform: "uppercase", marginRight: i === 1 ? Math.round(32 * f) : 0 }}>{h}</span>
                ))}
              </div>
              <div style={{ flex: 1, overflow: "hidden", padding: `${Math.round(4 * f)}px 0` }}>
                {evalTests.slice(0, evalRows).map((test, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", alignItems: "center", padding: `${Math.round(7 * f)}px ${Math.round(16 * f)}px`, borderBottom: "1px solid rgba(255,255,255,0.04)", gap: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: Math.round(9 * f) }}>
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.07 + 0.2, type: "spring" }} style={{ width: Math.round(18 * f), height: Math.round(18 * f), borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(9 * f), fontWeight: 700, background: test.status === "pass" ? "rgba(74,222,128,0.2)" : test.status === "warn" ? "rgba(201,168,76,0.25)" : "rgba(248,113,113,0.2)", border: `1px solid ${test.status === "pass" ? "rgba(74,222,128,0.35)" : test.status === "warn" ? GOLD_BORDER : "rgba(248,113,113,0.3)"}`, color: test.status === "pass" ? "#4ade80" : test.status === "warn" ? GOLD : "#f87171" }}>
                        {test.status === "pass" ? "✓" : test.status === "warn" ? "!" : "✗"}
                      </motion.div>
                      <span style={{ fontSize: Math.round(11 * f), color: "rgba(255,255,255,0.72)" }}>{test.query}</span>
                    </div>
                    <div style={{ width: Math.round(100 * f), height: Math.round(3 * f), background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden", marginRight: Math.round(14 * f) }}>
                      <motion.div style={{ height: "100%", background: test.status === "pass" ? "#4ade80" : test.status === "warn" ? GOLD : "#f87171", borderRadius: 2 }} initial={{ width: 0 }} animate={{ width: `${test.confidence}%` }} transition={{ duration: 0.9, delay: i * 0.07 + 0.25 }} />
                    </div>
                    <span style={{ fontSize: Math.round(11 * f), fontFamily: "monospace", color: test.status === "pass" ? "#4ade80" : test.status === "warn" ? GOLD : "#f87171", minWidth: Math.round(28 * f), textAlign: "right" }}>{test.confidence}%</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {activeStep === 4 && (
            <motion.div key="deploy" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }} style={{ padding: `${Math.round(24 * f)}px ${Math.round(20 * f)}px`, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: Math.round(24 * f) }}>
              <p style={{ margin: 0, fontSize: Math.round(9 * f), color: GOLD, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "monospace" }}>Deployment Pipeline</p>
              <div style={{ position: "relative", display: "flex", alignItems: "flex-start", justifyContent: "space-between", paddingTop: Math.round(18 * f) }}>
                <div style={{ position: "absolute", top: Math.round(18 * f), left: Math.round(14 * f), right: Math.round(14 * f), height: 2, background: "rgba(255,255,255,0.07)", borderRadius: 1 }}>
                  <motion.div style={{ height: "100%", background: GOLD, borderRadius: 1 }} initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 2.4, ease: "easeInOut" }} />
                </div>
                {deploySteps.map((step, i) => (
                  <motion.div key={i} initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.22, type: "spring" }} style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: Math.round(9 * f) }}>
                    <motion.div animate={step.active ? { boxShadow: [`0 0 0 0 rgba(201,168,76,0.4)`, `0 0 0 ${Math.round(10 * f)}px rgba(201,168,76,0)`, `0 0 0 0 rgba(201,168,76,0)`] } : {}} transition={{ duration: 1.8, repeat: Infinity }} style={{ width: Math.round(34 * f), height: Math.round(34 * f), borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: step.done ? Math.round(13 * f) : Math.round(14 * f), background: step.done ? GOLD : step.active ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.07)", border: `2px solid ${step.done || step.active ? GOLD : "rgba(255,255,255,0.15)"}`, color: step.done ? "#0a0a0a" : step.active ? GOLD : "rgba(255,255,255,0.3)", fontWeight: step.done ? 700 : 400 }}>
                      {step.done ? "✓" : step.label === "Create Bot" ? "◆" : step.label === "Upload KB" ? "◈" : step.label === "Gen Prompts" ? "✦" : "▲"}
                    </motion.div>
                    <span style={{ fontSize: Math.round(9 * f), color: step.done || step.active ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.25)", whiteSpace: "nowrap" }}>{step.label}</span>
                  </motion.div>
                ))}
              </div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} style={{ padding: `${Math.round(11 * f)}px ${Math.round(14 * f)}px`, borderRadius: Math.round(9 * f), background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, display: "flex", alignItems: "center", gap: Math.round(12 * f) }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }} style={{ width: Math.round(24 * f), height: Math.round(24 * f), borderRadius: "50%", border: `2px solid ${GOLD_BORDER}`, borderTop: `2px solid ${GOLD}`, flexShrink: 0 }} />
                <div>
                  <p style={{ margin: `0 0 ${Math.round(2 * f)}px`, fontSize: Math.round(12 * f), fontWeight: 500, color: "rgba(255,255,255,0.88)" }}>Preparing your assistant</p>
                  <p style={{ margin: 0, fontSize: Math.round(10 * f), color: "rgba(255,255,255,0.3)" }}>Finalizing configuration · 94% complete</p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div style={{ padding: `${Math.round(9 * f)}px ${Math.round(14 * f)}px`, background: "rgba(0,0,0,0.6)", borderTop: "1px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", gap: 0, overflow: "hidden" }}>
        <span style={{ color: GOLD, fontFamily: "monospace", fontSize: Math.round(11 * f), marginRight: Math.round(8 * f), flexShrink: 0 }}>$</span>
        <AnimatePresence mode="wait">
          <motion.span key={activeStep} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} style={{ fontFamily: "monospace", fontSize: Math.round(11 * f), color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap", flexShrink: 0 }}>
            {STEPS[activeStep].terminalTitle}
          </motion.span>
        </AnimatePresence>
        <motion.span animate={{ opacity: [1, 0, 1] }} transition={{ duration: 1, repeat: Infinity }} style={{ display: "inline-block", width: 2, height: Math.round(13 * f), background: GOLD, borderRadius: 1, marginLeft: Math.round(4 * f), flexShrink: 0 }} />
      </div>
    </div>
  );
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [showEmailError, setShowEmailError] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [fullName, setFullName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(60);
  const [verifying, setVerifying] = useState(false);
  const [toast, setToast] = useState(null);
  const [signupStep, setSignupStep] = useState(1);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [activeStep, setActiveStep] = useState(3);
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const scale = getScale(windowWidth);
  const f = scale.factor;

  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(s => (s + 1) % STEPS.length);
    }, 4500);
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    if (otpSent && timer > 0) {
      const t = setTimeout(() => setTimer(p => p - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [otpSent, timer]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);
  useEffect(() => {
  console.log("isSignup changed:", isSignup);
}, [isSignup]);

useEffect(() => {
  console.log("signupStep changed:", signupStep);
}, [signupStep]);

  const showToast = useCallback((msg, type = "success") => setToast({ msg, type }), []);

  const handleGoogleLogin = async (credential) => {
    try {
      const res = await fetch(`${getApiBase()}/google-login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: credential }) });
      const data = await res.json();
      if (!res.ok) { showToast(data?.detail || "Google login failed", "error"); return; }
      localStorage.setItem("token", data.token);
      localStorage.setItem("tenant_id", data.config.tenantid);
      localStorage.setItem("company_name", data.config.tenantname);
      localStorage.setItem("fullname", data.fullname);
      localStorage.setItem("role", data.role || "member"); 
      showToast("Welcome!", "success");
      setTimeout(() => navigate(`/dashboard/${data.config.tenantid}`), 800);
    } catch { showToast("Connection error", "error"); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email) { setShowEmailError(true); return; }
    try {
      const res = await fetch(`${getApiBase()}/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) { showToast(data?.detail === "Use Google login" ? "Use Google to sign in" : "Invalid credentials", "error"); return; }
      if (data?.token) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("tenant_id", data.config.tenantid);
        localStorage.setItem("company_name", data.config.tenantname);
        localStorage.setItem("fullname", data.fullname);
        localStorage.setItem("role", data.role || "member"); 
        showToast("Redirecting to Dashboard...", "success");
        setTimeout(() => navigate(`/dashboard/${data.config.tenantid}`), 800);
      }
    } catch { showToast("Connection error", "error"); }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { showToast("Passwords do not match", "error"); return; }
    if (password.length < 8) { showToast("Password must be at least 8 characters", "error"); return; }
    try {
      const res = await fetch(`${getApiBase()}/signup`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ fullname: fullName, tenant_name: tenantName, email, password, confirm_password: confirmPassword }) });
      const data = await res.json();
      if (!res.ok) { const err = data?.detail; showToast(typeof err === "object" ? err.msg || "Signup failed" : err || "Signup failed", "error"); return; }
      localStorage.setItem("tenant_id", data.tenant_id);
      if (data.company_name) localStorage.setItem("company_name", data.company_name);
      setTenantName(""); setFullName(""); setEmail(""); setPassword(""); setConfirmPassword("");
      setIsSignup(false); setSignupStep(1);
      showToast("Account created! Sign in to continue", "success");
    } catch { showToast("Server error", "error"); }
  };

  const sendOtp = async () => {
  if (!email || !tenantName || !fullName) {
    showToast("Please fill in all fields", "error");
    return;
  }

  setSendingOtp(true);

  try {
    const res = await fetch(`${getApiBase()}/send-otp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await res.json();

    if (!res.ok) {
  if (data?.detail === "Email already registered") {
    showToast("Email already registered. Please sign in.", "error");

    setTenantName("");
    setPassword("");
    setConfirmPassword("");
    setOtp("");
    setOtpSent(false);
    setTimer(60);
    setSignupStep(1);

    setIsSignup(false);

    return;
  }

  showToast(data?.detail || "Failed to send OTP", "error");
  return;
}

    setOtpSent(true);
    setTimer(60);
    setSignupStep(2);

  } catch {
    showToast("Server error", "error");
  } finally {
    setSendingOtp(false);
  }
};

  const verifyOtp = async () => {
    setVerifying(true);
    try {
      const res = await fetch(`${getApiBase()}/verify-otp`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email, otp }) });
      const data = await res.json();
      if (!res.ok) { showToast(data?.detail || "Invalid code", "error"); return false; }
      showToast("Verified!", "success"); return true;
    } catch { showToast("Verification failed", "error"); return false; } finally { setVerifying(false); }
  };

  const inputPadV = Math.round(13 * f);
  const inputPadH = Math.round(14 * f);
  const inputFontSize = Math.round(14 * f);
  const inputBorderRadius = Math.round(8 * f);

  const inputStyle = {
    width: "100%",
    padding: `${inputPadV}px ${inputPadH}px ${inputPadV}px ${Math.round(42 * f)}px`,
    background: "#1a1a1a",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: inputBorderRadius,
    fontSize: inputFontSize,
    color: "#C0C0C0",
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  const labelStyle = {
    fontFamily: "monospace",
    fontSize: Math.round(10 * f),
    color: "rgb(255, 254, 254)",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    display: "block",
    marginBottom: Math.round(8 * f),
  };

  const leftPadding = scale.tier === "ultrawide" ? "56px 80px" : scale.tier === "fullhd" ? "40px 60px" : scale.tier === "large" ? "32px 48px" : scale.tier === "desktop" ? "28px 40px" : "24px 32px";
  const rightPadding = scale.tier === "ultrawide" ? "48px 80px" : scale.tier === "fullhd" ? "40px 64px" : scale.tier === "large" ? "36px 52px" : scale.tier === "desktop" ? "32px 44px" : "28px";
  const headingSize = Math.round(40 * f);
  const subheadingSize = Math.round(36 * f);
  const maxFormWidth = Math.round(420 * f);
  const logoHeight = Math.round(42 * f);

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      width: "100%",
      overflowX: "hidden",
    }}>
      <style>{`
        input::placeholder, textarea::placeholder {
          color: rgba(255,255,255,0.12) !important;
          opacity: 1;
        }
        input::-webkit-input-placeholder, textarea::-webkit-input-placeholder {
          color: rgba(255,255,255,0.12) !important;
        }
        input::-moz-placeholder, textarea::-moz-placeholder {
          color: rgba(255,255,255,0.12) !important;
        }
        input:-ms-input-placeholder, textarea:-ms-input-placeholder {
          color: rgba(255,255,255,0.12) !important;
        }
      `}</style>
      <div style={{
        flex: 1,
        maxWidth: "58%",
        minWidth: 0,
        background: "#0a0a0a",
        display: "flex",
        flexDirection: "column",
        padding: leftPadding,
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -200, left: -200, width: Math.round(500 * f), height: Math.round(500 * f), background: "rgba(201,168,76,0.05)", borderRadius: "50%", filter: `blur(${Math.round(100 * f)}px)`, pointerEvents: "none" }} />
        {scale.tier !== "laptop" && (
          <div style={{ position: "absolute", bottom: -100, right: -100, width: Math.round(400 * f), height: Math.round(400 * f), background: "rgba(201,168,76,0.03)", borderRadius: "50%", filter: `blur(${Math.round(80 * f)}px)`, pointerEvents: "none" }} />
        )}

        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: Math.round(10 * f) }}>
          <a href="https://droog.io/" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "flex-start", gap: Math.round(4 * f), textDecoration: "none", position: "relative" }}>
            <img src="/Droog_Logo-removebg-preview.png" alt="Droog Logo" style={{ height: `${logoHeight}px`, width: "auto", objectFit: "contain", filter: "drop-shadow(0 6px 18px rgba(201,168,76,0.18))" }} />
            <motion.div animate={{ y: [0, -2, 0], boxShadow: ["0 0 0 rgba(201,168,76,0.10)", "0 0 10px rgba(201,168,76,0.28)", "0 0 0 rgba(201,168,76,0.10)"], scale: [1, 1.04, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} style={{ position: "relative", marginTop: -2, marginLeft: Math.round(-6 * f), fontSize: `${Math.round(8 * f)}px`, fontFamily: "monospace", color: GOLD, background: "rgba(201,168,76,0.10)", border: `1px solid ${GOLD_BORDER}`, borderRadius: 999, padding: `2px ${Math.round(7 * f)}px`, letterSpacing: "0.08em", fontWeight: 700, lineHeight: 1 }}>AI</motion.div>
          </a>
        </div>

        <div style={{ width: "100%", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: Math.round(8 * f), marginBottom: Math.round(14 * f) }}>
            <div style={{ width: Math.round(24 * f), height: 1, background: GOLD }} />
            <span style={{ fontFamily: "monospace", fontSize: `${Math.round(10 * f)}px`, color: "rgba(255,255,255,0.4)", letterSpacing: "0.15em", textTransform: "uppercase" }}>Conversational AI Platform</span>
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: `${headingSize}px`, fontWeight: 700, lineHeight: 1.05, color: "rgba(255,255,255,0.95)", margin: `0 0 ${Math.round(20 * f)}px`, letterSpacing: "-0.02em" }}>
            Build AI assistants your customers{" "}
            <span style={{ color: GOLD, fontStyle: "italic" }}>actually trust.</span>
          </h1>

          {scale.tier !== "laptop" && (
            <div style={{ display: "flex", gap: Math.round(16 * f), marginBottom: Math.round(20 * f), flexWrap: "wrap" }}>
              {STEPS.map((step, i) => (
                <motion.div
                  key={i}
                  onClick={() => setActiveStep(i)}
                  whileHover={{ scale: 1.04 }}
                  style={{
                    padding: `${Math.round(5 * f)}px ${Math.round(12 * f)}px`,
                    borderRadius: 999,
                    fontSize: Math.round(10 * f),
                    fontFamily: "monospace",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    transition: "all 0.25s",
                    background: activeStep === i ? GOLD_DIM : "rgba(255,255,255,0.04)",
                    border: `1px solid ${activeStep === i ? GOLD_BORDER : "rgba(255,255,255,0.07)"}`,
                    color: activeStep === i ? GOLD : "rgba(255,255,255,0.3)",
                  }}
                >
                  {step.label}
                </motion.div>
              ))}
            </div>
          )}

          <DemoWindow activeStep={activeStep} scale={scale} />
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: Math.round(20 * f) }}>
          <span style={{ fontFamily: "monospace", fontSize: `${Math.round(9 * f)}px`, color: "rgba(255,255,255,0.2)", letterSpacing: "0.06em" }}>v1.0 · droog.io</span>
        </div>
      </div>

      <div style={{
        flex: 1,
        maxWidth: "42%",
        minWidth: 320,
        background: "#0f0e0e",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: rightPadding,
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -150, right: -150, width: Math.round(400 * f), height: Math.round(400 * f), background: "rgba(90,50,130,0.08)", borderRadius: "50%", filter: `blur(${Math.round(80 * f)}px)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -100, left: -100, width: Math.round(300 * f), height: Math.round(300 * f), background: "rgba(201,168,76,0.04)", borderRadius: "50%", filter: `blur(${Math.round(60 * f)}px)`, pointerEvents: "none" }} />

        {scale.tier !== "laptop" && (
          <div style={{ position: "absolute", top: Math.round(24 * f), right: Math.round(24 * f), display: "flex", alignItems: "center", gap: Math.round(6 * f) }}>
          </div>
        )}

        <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: maxFormWidth }}>
          <AnimatePresence mode="wait">
            <motion.h2 key={isSignup ? "signup" : "login"} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: `${subheadingSize}px`, fontWeight: 700, color: "rgba(255,255,255,0.92)", margin: `0 0 ${Math.round(10 * f)}px`, letterSpacing: "-0.02em", textAlign: "center" }}>
              {isSignup ? "Join Droog." : "Welcome"}
            </motion.h2>
          </AnimatePresence>

          {scale.tier !== "laptop" && !isSignup && (
            <p style={{ textAlign: "center", fontFamily: "monospace", fontSize: Math.round(10 * f), color: "rgba(255,255,255,0.25)", letterSpacing: "0.06em", margin: `0 0 ${Math.round(20 * f)}px` }}>
              Sign in to your workspace
            </p>
          )}

          <AnimatePresence mode="wait">
            {toast && (
              <motion.div initial={{ opacity: 0, y: -6, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.97 }} style={{ marginBottom: Math.round(20 * f), padding: `${Math.round(12 * f)}px ${Math.round(16 * f)}px`, borderRadius: Math.round(8 * f), fontSize: Math.round(13 * f), textAlign: "center", background: toast.type === "error" ? "rgba(220,50,50,0.1)" : "rgba(50,200,100,0.1)", color: toast.type === "error" ? "#f87171" : "#4ade80", border: `1px solid ${toast.type === "error" ? "rgba(220,50,50,0.2)" : "rgba(50,200,100,0.2)"}` }}>
                {toast.msg}
              </motion.div>
            )}
          </AnimatePresence>

          {isSignup && signupStep > 1 && signupStep < 4 && (
            <div style={{ display: "flex", alignItems: "center", gap: Math.round(6 * f), marginBottom: Math.round(28 * f), justifyContent: "center" }}>
              {[1, 2, 3].map(step => (
                <div key={step} style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ width: signupStep >= step ? Math.round(20 * f) : Math.round(8 * f), height: Math.round(4 * f), borderRadius: 4, background: signupStep >= step ? GOLD : "rgba(255,255,255,0.1)", transition: "all 0.3s" }} />
                  {step < 3 && <div style={{ width: Math.round(6 * f) }} />}
                </div>
              ))}
            </div>
          )}

          <form
  key={isSignup ? "signup-form" : "login-form"}
  onSubmit={isSignup ? handleSignup : handleLogin}
>
            {!isSignup && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                <div style={{ marginBottom: Math.round(20 * f) }}>
                  <label style={labelStyle}>Email Address</label>
                  <div style={{ position: "relative" }}>
                    <FaEnvelope style={{ position: "absolute", left: Math.round(14 * f), top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.25)", fontSize: Math.round(13 * f) }} />
                    <input type="email" value={email} onChange={e => { setEmail(e.target.value); if (e.target.value) setShowEmailError(false); }} placeholder="you@company.com" style={{ ...inputStyle }} onFocus={e => e.target.style.borderColor = GOLD_BORDER} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                  </div>
                  {showEmailError && !email && <p style={{ margin: `${Math.round(6 * f)}px 0 0`, fontSize: Math.round(12 * f), color: "#f87171" }}>Email required</p>}
                </div>

                <div style={{ marginBottom: Math.round(8 * f) }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: Math.round(8 * f) }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>Password</label>
                    <motion.button whileHover={{ scale: 1.04 }} type="button" onClick={async () => { if (!email) { setShowEmailError(true); return; } try { const res = await fetch(`${getApiBase()}/forgot-password`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) }); if (res.ok) showToast("Reset link sent to your email", "success"); } catch {} }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: Math.round(12 * f), color: GOLD, padding: 0, fontFamily: "inherit" }}>
                      Forgot password?
                    </motion.button>
                  </div>
                  <div style={{ position: "relative" }}>
                    <FaLock style={{ position: "absolute", left: Math.round(14 * f), top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.25)", fontSize: Math.round(13 * f) }} />
                    <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" style={{ ...inputStyle, paddingRight: Math.round(44 * f) }} onFocus={e => e.target.style.borderColor = GOLD_BORDER} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: Math.round(14 * f), top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center" }}>
                      {showPassword ? <FaEyeSlash size={Math.round(15 * f)} /> : <FaEye size={Math.round(15 * f)} />}
                    </button>
                  </div>
                </div>

                <motion.button
  whileHover={{
    scale: 1.02,
    y: -2,
    boxShadow: "0 14px 32px rgba(201,168,76,0.20)",
    borderColor: "rgba(201,168,76,0.42)",
  }}
  whileTap={{ scale: 0.98 }}
  type="submit"
  style={{
    width: "60%",
    margin: "0 auto",
    padding: `${Math.round(10 * f)}px ${Math.round(18 * f)}px`,
    background:
      "linear-gradient(135deg, rgba(255,248,220,0.95) 0%, rgba(248,231,176,0.92) 35%, rgba(235,210,120,0.88) 70%, rgba(201,168,76,0.82) 100%)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: "1px solid rgba(201,168,76,0.28)",
    borderRadius: Math.round(12 * f),
    color: "#1f1a10",
    fontSize: Math.round(15 * f),
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
    marginTop: Math.round(20 * f),
    marginBottom: Math.round(16 * f),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: Math.round(10 * f),
    letterSpacing: "0.02em",
    position: "relative",
    overflow: "hidden",
    transition: "all 0.3s ease",
  }}
>
  <motion.div
    initial={{ left: "-60%" }}
    whileHover={{ left: "140%" }}
    transition={{
      duration: 0.9,
      ease: "easeInOut",
    }}
    style={{
      position: "absolute",
      top: 0,
      height: "100%",
      width: "45%",
      background:
        "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
      transform: "skewX(-25deg)",
      zIndex: 1,
      pointerEvents: "none",
    }}
  />

  <div
    style={{
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))",
      borderRadius: Math.round(14 * f),
      zIndex: 0,
    }}
  />

  <span style={{ position: "relative", zIndex: 2 }}>
    Sign in
  </span>

  <motion.div
    animate={{ x: [0, 4, 0] }}
    transition={{
      duration: 1.2,
      repeat: Infinity,
      ease: "easeInOut",
    }}
    style={{ position: "relative", zIndex: 2 }}
  >
    <FaArrowRight size={Math.round(14 * f)} />
  </motion.div>
</motion.button>

                <div style={{ display: "flex", alignItems: "center", gap: Math.round(14 * f), margin: `${Math.round(4 * f)}px 0 ${Math.round(16 * f)}px` }}>
                  <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.07)" }} />
                  <span style={{ fontFamily: "monospace", fontSize: Math.round(10 * f), color: "rgba(255,255,255,0.25)", letterSpacing: "0.1em" }}>OR</span>
                  <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.07)" }} />
                </div>

<div style={{
  width: "60%",
  margin: "0 auto",
  borderRadius: Math.round(8 * f),
  overflow: "hidden",
  border: "1px solid rgba(255,255,255,0.1)"
}}>
                    <GoogleLogin onSuccess={res => handleGoogleLogin(res.credential)} onError={() => showToast("Google sign-in failed", "error")} text="signin_with" theme="filled_black" shape="rectangular" size="large" width="100%" />
                </div>
              </motion.div>
            )}

            {isSignup && signupStep === 1 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                <div style={{ marginBottom: Math.round(18 * f) }}>
                  <label style={labelStyle}>Organization</label>
                  <input value={tenantName} onChange={e => setTenantName(e.target.value)} placeholder="Your company name" style={{ ...inputStyle, paddingLeft: Math.round(14 * f) }} onFocus={e => e.target.style.borderColor = GOLD_BORDER} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                </div>
                <div style={{ marginBottom: Math.round(18 * f) }}>
                  <label style={labelStyle}>Full Name</label>
                  <input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your Full Name"
                    style={{ ...inputStyle, paddingLeft: Math.round(14 * f) }}
                    onFocus={(e) => (e.target.style.borderColor = GOLD_BORDER)}
                    onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                  />
                </div>
                <div style={{ marginBottom: Math.round(20 * f) }}>
                  <label style={labelStyle}>Work Email</label>
                  <div style={{ position: "relative" }}>
                    <FaEnvelope style={{ position: "absolute", left: Math.round(14 * f), top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.25)", fontSize: Math.round(13 * f) }} />
                    <input type="email" value={email} onChange={e => { setEmail(e.target.value); if (e.target.value) setShowEmailError(false); }} placeholder="you@company.com" style={{ ...inputStyle }} onFocus={e => e.target.style.borderColor = GOLD_BORDER} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                  </div>
                </div>
                <motion.button whileHover={{ scale: 1.01, filter: "brightness(1.08)" }} whileTap={{ scale: 0.98 }} type="button" disabled={sendingOtp || !tenantName || !fullName || !email} onClick={sendOtp} style={{ width: "60%",
  margin: "0 auto", padding: `${Math.round(15 * f)}px`, background: sendingOtp || !tenantName || !fullName || !email ? "rgba(201,168,76,0.4)" : GOLD, border: "none", borderRadius: Math.round(8 * f), color: "#0a0a0a", fontSize: Math.round(15 * f), fontWeight: 700, cursor: sendingOtp || !tenantName || !fullName || !email ? "not-allowed" : "pointer", fontFamily: "inherit", marginBottom: Math.round(16 * f), display: "flex", alignItems: "center", justifyContent: "center", gap: Math.round(8 * f) }}>
                  {sendingOtp ? "Sending code..." : <><span>Continue</span><FaArrowRight size={Math.round(13 * f)} /></>}
                </motion.button>
                <div style={{ display: "flex", alignItems: "center", gap: Math.round(14 * f), margin: `${Math.round(4 * f)}px 0 ${Math.round(16 * f)}px` }}>
                  <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.07)" }} />
                  <span style={{ fontFamily: "monospace", fontSize: Math.round(10 * f), color: "rgba(255,255,255,0.25)", letterSpacing: "0.1em" }}>OR</span>
                  <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.07)" }} />
                </div>
<div style={{
  width: "60%",
  margin: "0 auto",
  borderRadius: Math.round(8 * f),
  overflow: "hidden",
  border: "1px solid rgba(255,255,255,0.1)"
}}>
                    <GoogleLogin onSuccess={res => handleGoogleLogin(res.credential)} onError={() => showToast("Google sign-up failed", "error")} text="signup_with" theme="filled_black" shape="rectangular" size="large" width="100%" />
                </div>
              </motion.div>
            )}

            {isSignup && signupStep === 2 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                <div style={{ textAlign: "center", marginBottom: Math.round(24 * f) }}>
                  <p style={{ fontSize: Math.round(14 * f), color: "rgba(255,255,255,0.4)", margin: `0 0 ${Math.round(4 * f)}px` }}>We sent a code to</p>
                  <p style={{ fontSize: Math.round(14 * f), fontWeight: 600, color: GOLD, margin: 0 }}>{email}</p>
                </div>
                <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="––––––" maxLength={6} style={{ ...inputStyle, paddingLeft: Math.round(14 * f), textAlign: "center", letterSpacing: "0.5em", fontSize: Math.round(20 * f), fontFamily: "monospace" }} onFocus={e => e.target.style.borderColor = GOLD_BORDER} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} type="button" disabled={verifying || otp.length !== 6} onClick={async () => { if (await verifyOtp()) setSignupStep(3); }} style={{ width: "100%", padding: `${Math.round(15 * f)}px`, background: verifying || otp.length !== 6 ? "rgba(201,168,76,0.4)" : GOLD, border: "none", borderRadius: Math.round(8 * f), color: "#0a0a0a", fontSize: Math.round(15 * f), fontWeight: 700, cursor: verifying || otp.length !== 6 ? "not-allowed" : "pointer", fontFamily: "inherit", marginTop: Math.round(16 * f) }}>
                  {verifying ? "Verifying..." : "Verify Code"}
                </motion.button>
                <p style={{ textAlign: "center", fontSize: Math.round(12 * f), color: "rgba(255,255,255,0.35)", marginTop: Math.round(14 * f) }}>
                  {timer > 0 ? `Resend in ${timer}s` : <button type="button" onClick={() => { sendOtp(); setTimer(60); }} style={{ background: "none", border: "none", cursor: "pointer", color: GOLD, fontSize: Math.round(12 * f), fontFamily: "inherit" }}>Resend code</button>}
                </p>
              </motion.div>
            )}

            {isSignup && signupStep === 3 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                <div style={{ marginBottom: Math.round(18 * f) }}>
                  <label style={labelStyle}>Create Password</label>
                  <div style={{ position: "relative" }}>
                    <FaLock style={{ position: "absolute", left: Math.round(14 * f), top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.25)", fontSize: Math.round(13 * f) }} />
                    <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" style={{ ...inputStyle, paddingRight: Math.round(44 * f) }} onFocus={e => e.target.style.borderColor = GOLD_BORDER} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: Math.round(14 * f), top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.3)" }}>
                      {showPassword ? <FaEyeSlash size={Math.round(15 * f)} /> : <FaEye size={Math.round(15 * f)} />}
                    </button>
                  </div>
                </div>
                <div style={{ marginBottom: Math.round(16 * f) }}>
                  <label style={labelStyle}>Confirm Password</label>
                  <input type={showPassword ? "text" : "password"} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" style={{ ...inputStyle, paddingLeft: Math.round(14 * f) }} onFocus={e => e.target.style.borderColor = GOLD_BORDER} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: `${Math.round(6 * f)}px ${Math.round(16 * f)}px`, marginBottom: Math.round(20 * f) }}>
                  {[{ label: "Uppercase", test: /[A-Z]/.test(password) }, { label: "Lowercase", test: /[a-z]/.test(password) }, { label: "Number", test: /\d/.test(password) }, { label: "8+ chars", test: password.length >= 8 }].map(({ label, test }) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: Math.round(6 * f), fontSize: Math.round(11 * f), color: test ? "#4ade80" : "rgba(255,255,255,0.3)" }}>
                      <div style={{ width: Math.round(14 * f), height: Math.round(14 * f), borderRadius: "50%", border: `1px solid ${test ? "rgba(74,222,128,0.5)" : "rgba(255,255,255,0.15)"}`, background: test ? "rgba(74,222,128,0.15)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {test && <FaCheck size={Math.round(7 * f)} color="#4ade80" />}
                      </div>
                      {label}
                    </div>
                  ))}
                </div>
                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} type="submit" disabled={!password || password !== confirmPassword || password.length < 8} style={{ width: "100%", padding: `${Math.round(15 * f)}px`, background: !password || password !== confirmPassword || password.length < 8 ? "rgba(201,168,76,0.4)" : GOLD, border: "none", borderRadius: Math.round(8 * f), color: "#0a0a0a", fontSize: Math.round(15 * f), fontWeight: 700, cursor: !password || password !== confirmPassword || password.length < 8 ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                  Create Account
                </motion.button>
              </motion.div>
            )}
          </form>

          <div style={{ marginTop: Math.round(24 * f), textAlign: "center", fontSize: Math.round(14 * f) }}>
            {isSignup ? (
              <span style={{ color: "rgba(255,255,255,0.4)" }}>
                Already have an account?{" "}
                <motion.span whileHover={{ scale: 1.04 }} onClick={() => { setIsSignup(false); setSignupStep(1); setShowEmailError(false); }} style={{ color: GOLD, cursor: "pointer", fontWeight: 500 }}>
                  Sign in
                </motion.span>
              </span>
            ) : (
              <span style={{ color: "rgba(255,255,255,0.4)" }}>
                New to Droog?{" "}
                <motion.span whileHover={{ scale: 1.04 }} onClick={() => { setIsSignup(true); setSignupStep(1); setShowEmailError(false); }} style={{ color: GOLD, cursor: "pointer", fontWeight: 500 }}>
                  Create your workspace →
                </motion.span>
              </span>
            )}
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            bottom: Math.round(28 * f),
            right: Math.round(32 * f),
            left: Math.round(32 * f),
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <p
            style={{
              fontFamily: "monospace",
              fontSize: Math.round(10 * f),
              color: "rgba(255,255,255,0.18)",
              textAlign: "center",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              margin: 0,
            }}
          >
            © 2026 Droog.io — Building the Future of Conversational AI
          </p>
        </div>
      </div>
    </div>
  );
}